import {
  type FundSource,
  HokkaidoR8HouseholderMynaApplicationResult as Result,
} from "schema/gen/es/chiikipoint/model/v2/model_pb";
import {
  type GetHokkaidoR8HouseholderMynaApplicationResponse,
  type HandleHokkaidoR8HouseholderMynaApplicationCallbackResponse,
  HandleHokkaidoR8HouseholderMynaApplicationCallbackResponse_Failed_Reason,
} from "schema/gen/es/chiikipoint/wallet/v2/service_pb";
import {
  HOKKAIDO_HOUSEHOLDER_CAMPAIGN_END_DATE,
  HOKKAIDO_MYNA_APPLICATION_PUBLIC_RELEASE_DATE,
} from "../../config";

const PREVIEW_STORAGE_KEY = "hokkaido-myna-application-preview";

export type HokkaidoMynaApplicationBannerType =
  | "apply" // ハガキ未着の世帯主向け導線
  | "checking" // 道の確認待ち
  | "hidden";

// マイナポータルでの手続きが判定まで進まなかった理由。どれも申請は記録されず、やり直せる
export type HokkaidoMynaApplicationFailedReason =
  | "cancelled"
  | "expired"
  | "invalidCard"
  | "error";

export type HokkaidoMynaApplicationResultView =
  | { type: "granted"; amount: bigint; fundSource?: FundSource }
  | { type: "checking" }
  | { type: "notHouseholder" }
  | { type: "outsideHokkaido" }
  | { type: "alreadyReceived" }
  | { type: "identityMismatch" }
  | { type: "failed"; reason: HokkaidoMynaApplicationFailedReason }
  | { type: "serverError" };

export type HokkaidoMynaApplicationCallbackState =
  | { type: "processing" }
  | { type: "redirect"; uri: string }
  | HokkaidoMynaApplicationResultView;

export function isHokkaidoMynaApplicationOpen(
  tenantSlug: string,
  now: Date,
): boolean {
  return (
    tenantSlug === "hokkaido" && now <= HOKKAIDO_HOUSEHOLDER_CAMPAIGN_END_DATE
  );
}

// 全体公開前の限定公開。URL を知っていれば誰でも申請できる前提で、端末単位の緩い制御で足りるとしている
export function enableHokkaidoMynaApplicationPreview(): void {
  localStorage.setItem(PREVIEW_STORAGE_KEY, "true");
}

export function isHokkaidoMynaApplicationHomeVisible(
  tenantSlug: string,
  now: Date,
): boolean {
  if (!isHokkaidoMynaApplicationOpen(tenantSlug, now)) return false;
  return (
    now >= HOKKAIDO_MYNA_APPLICATION_PUBLIC_RELEASE_DATE ||
    localStorage.getItem(PREVIEW_STORAGE_KEY) === "true"
  );
}

// 自動付与できず、道の職員による確認を待っている結果
export function isPendingPrefectureCheck(result: Result): boolean {
  return (
    result === Result.NO_CANDIDATE ||
    result === Result.AMBIGUOUS ||
    result === Result.MUNICIPALITY_MISMATCH ||
    result === Result.CODE_ALREADY_USED ||
    result === Result.UNKNOWN_PROVIDER
  );
}

export function getHokkaidoMynaApplicationBannerType(
  application: GetHokkaidoR8HouseholderMynaApplicationResponse | null,
): HokkaidoMynaApplicationBannerType {
  if (!application || application.received) {
    return "hidden";
  }
  const { result } = application;
  // 本人情報を更新すれば再申請できるので、不一致は未申請と同じ扱い
  if (result === undefined || result === Result.IDENTITY_MISMATCH) {
    return "apply";
  }
  if (isPendingPrefectureCheck(result)) {
    return "checking";
  }
  return "hidden";
}

export function getResultView(
  result: Result,
  amount: bigint,
  fundSource: FundSource | undefined,
): HokkaidoMynaApplicationResultView {
  if (isPendingPrefectureCheck(result)) {
    return { type: "checking" };
  }
  switch (result) {
    case Result.GRANTED:
    case Result.STAFF_GRANTED:
      return { type: "granted", amount, fundSource };
    case Result.ALREADY_RECEIVED:
      return { type: "alreadyReceived" };
    case Result.IDENTITY_MISMATCH:
      return { type: "identityMismatch" };
    case Result.NOT_HOUSEHOLDER:
      return { type: "notHouseholder" };
    case Result.OUTSIDE_HOKKAIDO:
      return { type: "outsideHokkaido" };
    default:
      return { type: "serverError" };
  }
}

export function getCallbackState(
  data: HandleHokkaidoR8HouseholderMynaApplicationCallbackResponse | undefined,
  error: unknown,
): HokkaidoMynaApplicationCallbackState {
  if (error) {
    return { type: "serverError" };
  }
  if (!data) {
    return { type: "processing" };
  }
  const { outcome } = data;
  switch (outcome.case) {
    case "pending":
      return { type: "processing" };
    case "redirect":
      return { type: "redirect", uri: outcome.value.uri };
    case "completed":
      return getResultView(
        outcome.value.result,
        outcome.value.amount,
        outcome.value.fundSource,
      );
    case "failed":
      return { type: "failed", reason: toFailedReason(outcome.value.reason) };
    default:
      return { type: "serverError" };
  }
}

function toFailedReason(
  reason: HandleHokkaidoR8HouseholderMynaApplicationCallbackResponse_Failed_Reason,
): HokkaidoMynaApplicationFailedReason {
  switch (reason) {
    case HandleHokkaidoR8HouseholderMynaApplicationCallbackResponse_Failed_Reason.CANCELLED:
      return "cancelled";
    case HandleHokkaidoR8HouseholderMynaApplicationCallbackResponse_Failed_Reason.EXPIRED:
      return "expired";
    case HandleHokkaidoR8HouseholderMynaApplicationCallbackResponse_Failed_Reason.INVALID_CARD:
      return "invalidCard";
    default:
      return "error";
  }
}
